import React from 'react'
import _ from 'underscore'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'

import Modal from 'components/Modal'

import useSearchParams from 'hooks/useSearchParams'
import { useStoreContext } from 'hooks/StoreContext'

import { alertCodeDisplayValue } from 'helpers/alerts'
import { longDateTime } from 'helpers/formatters'

import { ConsolidatedEvent } from 'types/ConsolidatedEvent'

import { color } from 'styles/colors'

type Props = {
  query: string | null;
}

const ConsolidatedEventModal = ({ query = null }: Props) => {
  const navigate = useNavigate()

  const id = useSearchParams('id')
  const { consolidatedEvents } = useStoreContext()

  const currentEvent = _.find<Array<ConsolidatedEvent>>(consolidatedEvents, (event) => event.id === id)

  return (
    <Modal isDisplayed={!!id && !!currentEvent} closeCallback={() => { navigate(`/alerts?q=${query}`) }}>
      {currentEvent && (
        <>
          <S.Header>
            <S.Title>{currentEvent.name}</S.Title>
            <S.Category>Consolidated Event</S.Category>
            <S.LastUpdated>Last Updated: <span>{longDateTime(currentEvent.updatedAt)}</span></S.LastUpdated>
          </S.Header>
          <S.Section>
            <S.SectionTitle>Problem</S.SectionTitle>
            <S.Details>
              <div>Diagnostics has detected a(n) {currentEvent.name.toLowerCase()}</div>
              <div>The following alerts may occur because of this event:</div>
              <div>{currentEvent.effectAlerts.concat(currentEvent.causeAlerts).map((code) => alertCodeDisplayValue(code)).join(', ')}</div>
              <div>Note that you may also have received notifications for the triggering alerts</div>
            </S.Details>
          </S.Section>
          <S.Section>
            <S.SectionTitle>Possible causes and solutions</S.SectionTitle>
            <S.Details>
              <S.List itemsCount={currentEvent.possibleCauses.length}>
                {currentEvent.possibleCauses.map(({ description, details }) => (
                  <div key={description}>
                    <li>{description}</li>
                    {details &&
                      <ul>
                        {details.map(detail =>
                          <li key={detail}>{detail}</li>
                        )}
                      </ul>
                    }
                  </div>
                ))}
              </S.List>
            </S.Details>
          </S.Section>
        </>
      )}
    </Modal>
  )
}

export default ConsolidatedEventModal

const S = {
  Header: styled.div`
    margin-top: 10px;
    margin-bottom: 20px;
  `,
  Title: styled.div`
    color: ${color('headerText')};
    font-size: 25px;
    font-weight: 600;
    padding-left: 20px;
    margin-top: -10px;
    line-height: normal;
  `,
  Category: styled.div`
    color: ${color('headerText')};
    font-size: 18px;
    line-height: normal;
    padding-left: 20px;
    margin-bottom: 10px;
  `,
  LastUpdated: styled.span`
    font-size: 14px;
    color: ${color('secondaryText')};
    padding-left: 20px;
    padding-right: 20px;
    line-height: normal;
  `,
  Section: styled.div`
    padding-left: 25px;
  `,
  SectionTitle: styled.div`
    font-weight: bold;
    text-transform: capitalize;
  `,
  Details: styled.div`
    padding: 5px 0 15px 15px;
  `,
  List: styled.ul.withConfig({
    shouldForwardProp: (prop) => !['itemsCount'].includes(prop)
  })<{ itemsCount: number }>`
    list-style-type: ${({ itemsCount }) => itemsCount > 1 ? 'disc' : 'none'};
  `
}
